import { GameRefundDto } from '../../../models/game/dto/game.dto';
import { RefundStatus } from '../../../models/game/dto/refund.dto';
import {
    PaymentIntentDto,
    PaymentIntentStatus,
} from '../../../models/payment/dto/payment-intent.dto';

/**
 * What the game-details footer is allowed to call money.
 *
 * The footer used to read `PaymentIntent.amount` as "cash the venue holds",
 * and that identity fails in three ordinary ways — all of them visible on the
 * same screen, and all of them scaled by the capacity the booking was sold at:
 *
 *  1. **A partial refund leaves its intent in `succeeded` at its full amount.**
 *     That is deliberate (`refund-payment-intent.ts`), so the other shares of a
 *     pay-for-others batch stay refundable. A covered player leaving a €47,08
 *     four-seat batch writes an €11,77 `Refund` and the footer still read
 *     €47,08 — with the refund listed in the card directly below it.
 *  2. **A full refund flips the intent to `refunded`**, which the footer already
 *     dropped from "paid" — but it kept counting it as EXPECTED, so a fully
 *     refunded booking reported its whole amount as still owed.
 *  3. **`amount` is the quote, not the charge.** `chargePayLaterBooking` settles
 *     a pay-later booking for whatever an operator agreed at the desk and
 *     records it in `lastChargedAmount`, leaving `amount` at the quote.
 *
 * The API's own rule for the same question lives in
 * `payment/service/cancel/refundable-remainder.ts`; this is its client half,
 * written against the fields the game DTO publishes.
 */

/**
 * Refund rows that represent money the organization has already committed to
 * returning — the client mirror of the API's `LIVE_REFUND_STATUSES`.
 *
 * `FAILED` is excluded on purpose: the retry cron sets it when an attempt hit a
 * PERMANENT error, i.e. the money never left and the venue still holds it.
 * `RETRY` counts — the row is the cron's queue, so the money is on its way.
 */
const LIVE_REFUND_STATUSES: readonly RefundStatus[] = [
    RefundStatus.SUCCESSFUL,
    RefundStatus.RETRY,
];

/** Intents whose money has gone back (or is going back) to the payer. */
const REVERSED_STATUSES: readonly PaymentIntentStatus[] = [
    PaymentIntentStatus.refunded,
    PaymentIntentStatus.refunding,
];

/**
 * Live refund cents per payment intent id.
 *
 * `GameRefundDto.amount` is in MAJOR units and is already the SETTLED figure
 * per row (the API applies `settledRefundAmountCents`), so a full row carries
 * the remainder rather than the whole intent and summing a intent's live rows
 * can never exceed what it charged.
 */
export function liveRefundCentsByPaymentIntentId(
    refunds: readonly GameRefundDto[],
): Map<string, number> {
    const byPi = new Map<string, number>();
    for (const refund of refunds) {
        if (!LIVE_REFUND_STATUSES.includes(refund.status)) continue;
        // An older API sends no `paymentIntentId`. Attributing such a row to
        // an arbitrary intent would be a guess about money, so it is skipped:
        // the footer then reports what it did before this rule existed rather
        // than something newly wrong.
        if (!refund.paymentIntentId) continue;
        const cents = Math.round(refund.amount * 100);
        byPi.set(refund.paymentIntentId, (byPi.get(refund.paymentIntentId) ?? 0) + cents);
    }
    return byPi;
}

/** Cents actually taken from the payer: the charge, falling back to the quote. */
export function chargedCents(paymentIntent: PaymentIntentDto): number {
    return paymentIntent.lastChargedAmount ?? paymentIntent.amount;
}

/**
 * What this payment intent is still worth to the venue, in cents — money it
 * holds, or (for an intent not yet settled) is still entitled to collect.
 *
 * Zero once the intent has been reversed in full; otherwise the charge less
 * whatever has already gone back. Clamped at zero: refund rows larger than the
 * recorded charge are a data problem, not a licence to report negative money.
 *
 * Statuses that never took money and were never reversed — `pay_later`,
 * `canceled`, the `requires_*` family — are deliberately left at their full
 * value here. Whether a canceled intent still represents a debt depends on
 * whether its player is still in the game, which this function cannot see; the
 * footer's existing treatment (expected, not paid) is preserved unchanged.
 */
export function retainedCents(
    paymentIntent: PaymentIntentDto,
    refundedCents: number,
): number {
    if (REVERSED_STATUSES.includes(paymentIntent.status)) return 0;
    return Math.max(0, chargedCents(paymentIntent) - refundedCents);
}
